package com.oasisfeng.invaluable.dsl

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.actor.CommitmentRendering
import com.oasisfeng.invaluable.actor.CommitmentSnapshot
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.LinkTopology
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.resolveLink
import com.oasisfeng.invaluable.meaning.ActorProfile
import com.oasisfeng.invaluable.meaning.Utf8
import com.oasisfeng.invaluable.then
import kotlin.jvm.JvmInline

/**
 * GraphV4 social facade over actor commitment journals and Inter-Actor attention routes.
 *
 * A post is only `cause -> content`. The author is the actor that commits to the post, while
 * actor-targeted delivery and relay are the global [ActorRoute] primitive.
 */
open class SocialDsl(projector: LinkProjector) : MetaDsl(projector) {

    /** Construct a label [Post] directly under this actor's profile anchor (`actor -> MEANING -> actor`) without publishing it. */
    fun ActorProfile.post(content: NestedLinkable): Post = Post(this, content)
    /** Publish [content] from this actor's `actor -> MEANING` content origin and back the resulting [Post]. */
    fun Actor.share(content: NestedLinkable, cost: Quantity) = Post(link.then(Dimensions.MEANING()), content).also { back(it, cost) }
    /** Attach [content] under [post] (as comment) and back the resulting comment [Post]. */
    fun Actor.comment(post: Post, content: NestedLinkable, cost: Quantity) = Post(post, content).also { back(it, cost) }
    /** Recommend [post] to another actor by creating and backing an [ActorRoute]. */
    fun Actor.recommend(post: Post, to: Actor, cost: Quantity): ActorRoute = route(post, to, cost)
    /** Reply through a route that reached this actor. */
    fun Actor.reply(delivery: ActorRoute, content: NestedLinkable, cost: Quantity): Post {
        require(this == delivery.recipient) { "Only the delivery recipient can reply through it." }
        return Post(delivery, content).also { back(it, cost) }
    }
    /** Endorse a route by backing the route Link directly; no separate Back topology is introduced. */
    fun Actor.endorse(delivery: ActorRoute, cost: Quantity): Commitment {
        require(this == delivery.recipient) { "Only the delivery recipient can endorse it." }
        return back(delivery, cost)
    }

    companion object {
        /** Render a complete Link as any structural social path currently recognized by this DSL. */
        fun renderSocialPath(link: NestedLink): LinkView? =
            Post.render(link) ?: ActorRoute.render(link)
        /** Render complete commitments whose targets are understood by this DSL. */
        fun renderSocialCommitments(snapshot: CommitmentSnapshot): CommitmentRendering<LinkView>
        = snapshot.renderCommitments(::renderSocialPath)
    }
}

/**
 * Social content attachment represented as `cause -> content`.
 *
 * An [Actor] can only cause its `actor -> MEANING` origin. Published content then uses that origin, another [Post]
 * (to complement), or an [ActorRoute] (to reply) as cause. Authorship is not encoded in the post topology; it is
 * derived from the actor that commits to this post Link.
 */
@JvmInline value class Post private constructor(override val link: NestedLink) : LinkView {

    companion object : LinkTopology<Post>(::Post) {
        /** Attach [content] under [cause], returning the canonical [Post] view for that topology. */
        operator fun invoke(cause: NestedLinkable, content: NestedLinkable): Post
        = cause.link.then(content.link).let { render(it) ?: error("Not a social post topology: $it") }
        /** A post has a social cause and a non-actor target; an actor cause can only enter `MEANING`. */
        override fun NestedLink.matchesTopology(): Boolean {
            val content = nestedTargetOrNull()?.takeUnless(Actor::matchNode) ?: return false
            val cause = parent().nestedOrNull() ?: return false
            return if (Actor.matchNode(cause)) content == Dimensions.MEANING()
            else render(cause) != null || ActorRoute.render(cause) != null || ActorProfile.render(cause) != null
        }
        /** Cause can be [Actor], [Post], [ActorRoute] or an [ActorProfile] anchor (for a label); actor content is `MEANING`. */
        internal fun isPostCause(cause: NestedLink): Boolean
        = Actor.render(cause) != null || render(cause) != null || ActorRoute.render(cause) != null || ActorProfile.render(cause) != null
    }

    /** Immediate cause of this post: an actor meaning-origin seed, another post, or an actor route. */
    fun cause(): NestedLink = link.parent().requireNested("Post cause")
    /** Complete content Link attached by this post. */
    fun content(): NestedLink = link.requireNestedTarget()
    /** Render content as UTF-8 text when it carries an octet lens; non-text content stays structural. */
    fun textOrNull(): String? = Utf8.render(content())

    override fun toString(): String = link.toString()
}

/**
 * Commitments currently known for one canonical direct expression in an actor's meaning space.
 *
 * This is a derived snapshot view, not persistent profile state. [commitments] contains every commitment in the
 * snapshot whose target is exactly [post], regardless of issuer, quantity, or how [post]'s content can be decoded.
 */
class ProfileEvidence internal constructor(val post: Post, val commitments: List<Commitment>) {
    /** Net quantity committed to this appearance in the snapshot. */
    val backing: Quantity = commitments.fold(Quantity.ZERO) { total, commitment -> total + commitment.quantity }

    /** Whether the indexed actor has made any commitment to this same expression. */
    val selfAttested: Boolean = requireNotNull(post.profile()).actor().let { actor ->
        commitments.any { it.issuer == actor }
    }
}

/** The actor profile anchor this post is a label under (`actor -> MEANING -> actor`), or null for content/nested/routed posts. */
fun Post.profile(): ActorProfile? = ActorProfile.render(cause())

/**
 * Collect evidence for every committed label directly under [actor]'s profile anchor (`actor -> MEANING -> actor`).
 *
 * Membership is purely topological: the post's cause must be exactly that profile anchor. Ordinary content posts (which
 * live in the `actor -> MEANING` origin one level above) are not labels and are excluded. Results are grouped by
 * canonical label Link in snapshot encounter order. Issuer, quantity, codec, and descendants neither admit nor exclude
 * a label; ranking one as the display name or another presentation is an application view.
 */
fun CommitmentSnapshot.profileOf(actor: Actor): List<ProfileEvidence> {
    val profile = ActorProfile(actor)
    val evidence = linkedMapOf<Post, MutableList<Commitment>>()
    for (commitment in commitments) {
        val post = Post.render(commitment.targetLink)?.takeIf { it.cause() == profile.link } ?: continue
        evidence.getOrPut(post) { mutableListOf() } += commitment
    }
    return evidence.map { (post, commitments) -> ProfileEvidence(post, commitments.toList()) }
}

/**
 * Business view over a projected social target observation.
 *
 * [complete] is the only typed social identity, known only when the full [Post] or [ActorRoute] topology is locally
 * visible. [cause] and [targetLink] expose independently visible endpoints so callers can keep useful social position
 * without fabricating partial [LinkView] objects.
 */
class ObservedSocialTarget internal constructor(
    val projection: LinkProjection,
    val complete: Observation<LinkView>,
    val cause: Observation<NestedLink>,
    val targetLink: Observation<NestedLink>,
)

/**
 * Observe a projected social target without synthesizing placeholder Links.
 *
 * This function reconstructs a complete [Post] or [ActorRoute] only when the necessary preimages are locally known.
 * Otherwise, [ObservedSocialTarget.cause] and [ObservedSocialTarget.targetLink] preserve independently known endpoints.
 */
fun LinkProjector.observeSocialTarget(projection: LinkProjection, resolver: ProjectedLinkRecordResolver): ObservedSocialTarget {
    val complete = resolveLink(projection, resolver).mapKnownOrInvalid(
        "Resolved social target does not match social topology.",
    ) { it.nestedOrNull()?.let(SocialDsl::renderSocialPath) }

    val record = resolver.recordFor(projection)
        ?: return ObservedSocialTarget(projection, complete, cause = Observation.Missing(projection),
            targetLink = Observation.Missing(projection))
    if (record.projection != projection)
        return invalidSocialTarget(projection, "Social target record returned for the wrong projection.")

    val causeProjection = record.parent
        ?: return invalidSocialTarget(projection, "Social target cannot have root cause.")
    val targetProjection = record.target
        ?: return invalidSocialTarget(projection, "Social target cannot target root.")

    return ObservedSocialTarget(projection, complete, cause = observeSocialCause(causeProjection, resolver),
        targetLink = observeSocialEndpoint(targetProjection, resolver))
}

private fun invalidSocialTarget(projection: LinkProjection, reason: String)
= ObservedSocialTarget(projection, complete = Observation.Invalid(reason),
    cause = Observation.Invalid(reason), targetLink = Observation.Invalid(reason))

private fun LinkProjector.observeSocialCause(projection: LinkProjection, resolver: ProjectedLinkRecordResolver): Observation<NestedLink>
= when (val resolved = resolveLink(projection, resolver)) {
    is Observation.Known -> {
        val cause = resolved.value.nestedOrNull()
            ?: return Observation.Invalid("Social target cause has root parent.")
        if (Post.isPostCause(cause)) Observation.Known(cause)
        else Observation.Invalid("Social target cause is not an actor, post, or actor route.")
    }
    is Observation.Missing -> resolved
    is Observation.Invalid -> resolved
}

private fun LinkProjector.observeSocialEndpoint(projection: LinkProjection, resolver: ProjectedLinkRecordResolver): Observation<NestedLink>
= when (val resolved = resolveLink(projection, resolver)) {
    is Observation.Known -> {
        val endpoint = resolved.value.nestedOrNull()
            ?: return Observation.Invalid("Social target has root target.")
        Observation.Known(endpoint)
    }
    is Observation.Missing -> resolved
    is Observation.Invalid -> resolved
}
